import React, { useCallback, useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useDataProvider,
  useNotify,
  ReferenceInput,
  AutocompleteInput,
  SimpleForm,
  Button,
} from "react-admin";
import { Box, CircularProgress } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { FeaturedPlaylist } from "types/graphql";
import { FeaturedPlaylistCard } from "./FeaturedPlaylistCard";
import { RESOURCE_TYPE as RT } from "../../../../lib/constants/resourceType";

type FilterParams = {
  name?: string;
  excludeIds?: string[];
};

export function FeaturedPlaylistsList() {
  const [items, setItems] = useState<FeaturedPlaylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>("");
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Fetch featured playlists on mount
  useEffect(() => {
    const fetchFeaturedPlaylists = async () => {
      try {
        const { data } = await dataProvider.getFeaturedPlaylists(
          RT.FEATURED,
          {}
        );
        if (Array.isArray(data)) {
          setItems(data);
        } else {
          setItems([]);
        }
      } catch (error) {
        notify("Error loading featured playlists", "error");
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPlaylists();
  }, [dataProvider, notify]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        try {
          await dataProvider.updateFeaturedPlaylistsOrder(RT.FEATURED, {
            items: newItems.map((item) => item.id),
          });
        } catch (error) {
          notify("Error updating order", "error");
        }
      }
    },
    [items, dataProvider, notify]
  );

  const handleAddPlaylist = useCallback(async () => {
    if (!selectedPlaylistId || selectedPlaylistId === "") {
      notify("Please select a playlist", "warning");
      return;
    }

    setIsAdding(true);
    try {
      await dataProvider.addFeaturedPlaylist(RT.FEATURED, {
        id: selectedPlaylistId,
      });
      notify("Playlist added to featured", "success");
      // Refresh the list
      const { data } = await dataProvider.getFeaturedPlaylists(RT.FEATURED, {});
      if (Array.isArray(data)) {
        setItems(data);
      }
      // Reset selection
      setSelectedPlaylistId("");
    } catch (error) {
      notify("Error adding playlist", "error");
    } finally {
      setIsAdding(false);
    }
  }, [dataProvider, notify, selectedPlaylistId]);

  const handleRemovePlaylist = useCallback(
    async (playlistId: string) => {
      setRemovingIds((prev) => [...prev, playlistId]);
      try {
        await dataProvider.removeFeaturedPlaylist(RT.FEATURED, {
          id: playlistId,
        });
        notify("Playlist removed from featured", "success");
        setItems((prev) => prev.filter((item) => item.id !== playlistId));
      } catch (error) {
        notify("Error removing playlist", "error");
      } finally {
        setRemovingIds((prev) => prev.filter((id) => id !== playlistId));
      }
    },
    [dataProvider, notify]
  );

  const filterToQuery = (searchText: string): FilterParams => {
    const filter: FilterParams = {};

    // Get list of already featured playlist IDs to exclude
    const excludeIds = items.map((item) => item.playlist.id);

    filter.name = searchText;
    filter.excludeIds = excludeIds;

    return filter;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} gridGap={8}>
        <SimpleForm
          toolbar={<></>}
          onSubmit={() => {}}
          style={{ width: "100%" }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 8, width: "100%" }}
          >
            <ReferenceInput
              label="Search Playlists"
              source="playlistId"
              reference={RT.PLAYLISTS}
              sort={{ field: "createdAt", order: "DESC" }}
              filterToQuery={filterToQuery}
              style={{ flex: 1 }}
              disabled={isAdding}
            >
              <AutocompleteInput
                optionText={(record: any) => record.name}
                fullWidth
                onSelect={(record: any) => {
                  setSelectedPlaylistId(record?.id || "");
                }}
              />
            </ReferenceInput>
            <Button
              label={isAdding ? "Adding..." : "Add Featured Playlist"}
              onClick={handleAddPlaylist}
              disabled={!selectedPlaylistId || isAdding}
              variant="contained"
              color="primary"
              startIcon={
                isAdding ? <CircularProgress size={20} /> : <AddIcon />
              }
            />
          </Box>
        </SimpleForm>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <FeaturedPlaylistCard
              key={item.id}
              id={item.id}
              item={item.playlist}
              onRemove={() => handleRemovePlaylist(item.id)}
              isRemoving={removingIds.includes(item.id)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}
